import { AutoDisableableDto, type AutoDisableableInterface } from '@zaoblako/shared/common/dtos/auto-disableable.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { HelpdeskTemplateDtoKeysEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.template.dto.keys.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { DeadlineFormDto, type DeadlineFormInterface } from '@zaoblako/shared/common/dtos/deadline/deadline-form.dto';
import { PlanningUnitFormDto, type PlanningUnitFormInterface } from '@zaoblako/shared/common/dtos/planning-unit/planning-unit-form.dto';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { HelpdeskTaskTypeFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { MasterMilestoneFindInterface } from '@zaoblako/shared/modules/catalog/dtos/master-milestone/master-milestone-find.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import { HelpdeskTaskPriorityEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.priority.enum';
import { HelpdeskPriorityI18nEnum } from '@zaoblako/shared/modules/helpdesk/i18n/helpdesk.priority.i18n.enum';
import { SecurityBackendValidatorsEnum } from '@zaoblako/shared/modules/security/enums/security-backend-validators.enum';
import { EavEntityFormDto, type EavEntityFormInterface } from '@zaoblako/shared/modules/eav/dtos/entity/eav-entity-form.dto';

import * as yup from 'yup';

/** Helpdesk task template create and update APIs request body */
export interface HelpdeskTaskTemplateFormInterface extends AutoDisableableInterface {
  name: string;
  description?: string;
  plannedMin?: number;
  priority?: HelpdeskTaskPriorityEnum;
  planning: PlanningUnitFormInterface;
  deadline: DeadlineFormInterface;
  responsible: UserFindInterface;
  userGroupAuditors?: UserGroupFindInterface[];
  userAuditors?: UserFindInterface[];
  /** External user auditors */
  externalUserAuditors?: ExternalUserFindInterface[];
  deal?: DealFindInterface;
  project?: ProjectFindInterface;
  client?: ClientFindInterface;
  externalUser?: ExternalUserFindInterface;
  taskType?: HelpdeskTaskTypeFindInterface;
  unit?: UnitFindInterface;
  masterMilestone?: MasterMilestoneFindInterface;
  eavEntity?: EavEntityFormInterface;
}

/** Helpdesk task template create and update APIs request body - DTO schema */
export const HelpdeskTaskTemplateFormDto = SerdeDtoClass.from<AutoDisableableInterface, HelpdeskTaskTemplateFormInterface>(
  AutoDisableableDto,
  HelpdeskTemplateDtoKeysEnum.HELPDESK_TASK_TEMPLATE_FORM_DTO,
  {
    name: {
      label: 'Имя',
      serializer: yup
        .string()
        .min(1, DtoMessageEnum.EMPTY_STRING),
    },
    description: {
      optional: true,
      label: 'Описание',
      serializer: yup
        .string()
        .min(1, DtoMessageEnum.EMPTY_STRING),
    },
    plannedMin: {
      optional: true,
      label: 'Запланированное время',
      serializer: yup
        .number()
        .integer(DtoMessageEnum.NOT_INTEGER)
        .min(0, 'Запланированное время в минутах не может быть отрицательным'),
    },
    priority: {
      optional: true,
      label: 'Приоритет',
      serializer: commonEnumValidator(HelpdeskTaskPriorityEnum, HelpdeskPriorityI18nEnum),
    },
    planning: {
      label: 'Планирование',
      nested: PlanningUnitFormDto,
      serializer: (_, nested) => nested,
    },
    deadline: {
      label: 'Дедлайн',
      nested: DeadlineFormDto,
      serializer: (_, nested) => nested,
    },
    responsible: {
      label: 'Ответственный',
      serializer: entityLikeSchema,
    },
    userGroupAuditors: {
      optional: true,
      label: 'Группы аудиторов',
      serializer: yup.array(entityLikeSchema),
    },
    userAuditors: {
      optional: true,
      label: 'Аудиторы',
      serializer: yup.array(entityLikeSchema),
    },
    externalUserAuditors: {
      optional: true,
      label: 'Внешние аудиторы',
      serializer: yup.array(entityLikeSchema),
      checkers: [SecurityBackendValidatorsEnum.EXTERNAL_USER_ASSERT_EXISTS_BY_DTO],
    },
    deal: {
      optional: true,
      label: 'Сделка',
      serializer: entityLikeSchema,
    },
    project: {
      optional: true,
      label: 'Проект',
      serializer: entityLikeSchema,
    },
    client: {
      optional: true,
      label: 'Клиент',
      serializer: entityLikeSchema,
    },
    externalUser: {
      optional: true,
      label: 'Внешний пользователь',
      serializer: entityLikeSchema,
    },
    taskType: {
      optional: true,
      label: 'Тип заявки',
      serializer: entityLikeSchema,
    },
    unit: {
      optional: true,
      label: 'БЕ',
      serializer: entityLikeSchema,
    },
    masterMilestone: {
      optional: true,
      label: 'Мастер-веха',
      serializer: entityLikeSchema,
    },
    eavEntity: {
      optional: true,
      label: 'Дополнительные реквизиты',
      nested: EavEntityFormDto,
      serializer: (_, nested) => nested,
    },
  },
);
